import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useGate, useStore} from 'effector-react';
import {Location} from 'history';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Redirect, useLocation} from 'react-router-dom';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {ReactComponent as ArrowButtonIcon} from 'assets/svg/arrow-bottom.svg';
import {ReactComponent as DatamatrixIcon} from 'assets/svg/digital.svg';
import {LanguageSelector} from 'components/common/LanguageSelector';
import {Button} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {Spinner} from 'components/ui-kit/Spinner';
import {apiClient} from 'core/apiClient';
import {LABELS} from 'core/constants/labels';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {isAuth$, loginError$, loginFx, loginRedirectFromPrevPath$} from 'models/auth';
import {cert$, cleanRegisterData, RegisterGate} from 'models/register';
import {DirectorRegistrationWithCertRequest} from 'types';
import {ProfileRole, ProfileType} from 'types/profile';
import {mapEnumToI18nOptions} from 'utils/select';

import {
    AuthContent,
    AuthDescription,
    AuthTitle,
    ButtonsContainer,
    CollapsableContainer,
    CustomContentContainer,
    CustomDatamatrixIcon,
    Field,
    FormFields,
    FormSubtitle,
    LanguageWrapper,
    MainContainer,
    ServiceTitle,
    SpinnerLayer,
} from './styled';

type FormField = {
    name: keyof DirectorRegistrationWithCertRequest;
    readonly?: boolean;
    options?: SelectOption<string>[];
    type?: 'password' | 'number';
    hidden?: boolean;
};

export function RegisterPage() {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.register.fields.' + key), [
        i18n.language,
    ]);

    const isAuth = useStore(isAuth$);
    const [addInfoCollapsed, setAddInfoCollapsed] = useState(false);
    const pending = useStore(loginFx.pending);
    const error = useStore(loginError$);
    const loginRedirectFromPrevPath = useStore(loginRedirectFromPrevPath$);
    const location = useLocation<{from: Location}>();
    const prevLocation = location.state?.from;
    const cert = useStore(cert$);
    useGate(RegisterGate);

    const schema = useMemo(
        () =>
            yup.object().shape({
                role: yup.string().required(translateValidates('required')),
                email: yup
                    .string()
                    .email(translateValidates('contact_email'))
                    .required(translateValidates('required')),
                password: yup
                    .string()
                    .min(6, translateValidates('password'))
                    .required(translateValidates('required')),
                password_confirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .test('password', translateValidates('confirm_password_mismatch'), function(
                        value,
                    ) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        return value === this.parent.password;
                    }),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .nullable()
                    .notRequired()
                    .matches(/^\+?\d{10,15}$/, translateValidates('phone')),
                type: yup.mixed<'EDS_DIRECTOR' | 'EMPLOYEE'>().notRequired(),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
    } = useForm<DirectorRegistrationWithCertRequest>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    useEffect(() => {
        if (!cert?.email) return;
        setValue('contact_email', cert.email);
        setValue('email', cert.email);
    }, []);

    const formFields: FormField[] = [
        {name: 'role', options: mapEnumToI18nOptions(ProfileRole, 'dictionaries.profileRole')},
        {name: 'email', readonly: !!cert?.email},
        {name: 'phone'},
        {name: 'password', type: 'password'},
        {name: 'password_confirm', type: 'password'},
    ];

    const onSubmit = async (data: DirectorRegistrationWithCertRequest) => {
        if (!cert) return;
        try {
            const payload: DirectorRegistrationWithCertRequest = {
                role: data.role,
                email: cert.email || data.email,
                password: data.password,
                phone: data.phone,
                type: ProfileType.EDS_DIRECTOR,
                first_name: cert.first_name,
                middle_name: cert.middle_name,
                last_name: cert.last_name,
                thumbprint: cert.thumbprint,
                legal_person: {
                    tin: cert.organization_tin,
                    country: cert.country,
                    company_name: cert.organization_name,
                    legal_address: cert.legal_address,
                },
            };

            await apiClient.post('uaa/web/register', payload);
            toast.success(t('pages.register.message.success'));
            // if (response.status !== 200) throw new Error('Something went wrong!');
            // doLoginWithCert({cert: {...cert, email: data.email}, id: organization.id});
            cleanRegisterData();
            history.push(ROUTE.LOGIN);
        } catch (error) {
            toast.error(error.message);
            console.log(error);
        }
    };

    if (isAuth || !cert) {
        return (
            <Redirect to={prevLocation && loginRedirectFromPrevPath ? location.state.from : '/'} />
        );
    }

    const controlRender = (
        controlEvents: ControllerRenderProps<DirectorRegistrationWithCertRequest>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
            ...(field.type && {type: field.type}),
        };

        const props = {...baseProps, ...controlEvents};

        if (field.name === 'role') {
            return (
                <div style={{paddingBottom: '2px'}}>
                    <Select
                        options={field.options}
                        {...baseProps}
                        onChange={(option: SelectOption<number>) =>
                            controlEvents.onChange(option.value)
                        }
                    />
                </div>
            );
        }
        return <Input {...props} />;
    };

    const ModalSpinner = () => (
        <SpinnerLayer style={{minHeight: 300}}>
            <Spinner />
        </SpinnerLayer>
    );

    return (
        <>
            <div>
                <ServiceTitle size={4}>
                    <CustomDatamatrixIcon source={DatamatrixIcon} width={24} />
                    {LABELS.name}
                </ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <AuthContent>
                    <AuthTitle size={3}>{t('pages.register.title')}</AuthTitle>
                    <AuthDescription>{t('pages.register.description')}</AuthDescription>
                    <CustomContentContainer>
                        {pending ? (
                            <ModalSpinner />
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <FormFields>
                                    {formFields.map((field) => (
                                        <Field
                                            key={field.name}
                                            text={(errors?.[field.name] as FieldError)?.message}
                                            isError={!!errors?.[field.name]}
                                        >
                                            <span>{t(`pages.register.fields.${field.name}`)}</span>
                                            <Controller
                                                name={field.name}
                                                control={control}
                                                defaultValue=""
                                                render={(props) => controlRender(props, field)}
                                            />
                                        </Field>
                                    ))}
                                </FormFields>
                                <FormSubtitle
                                    collapsed={addInfoCollapsed}
                                    onClick={() => setAddInfoCollapsed(!addInfoCollapsed)}
                                >
                                    {t('pages.register.certificate_details')}
                                    <Icon source={ArrowButtonIcon} width={10} height={6} />
                                </FormSubtitle>
                                <CollapsableContainer collapsed={addInfoCollapsed}>
                                    <FormFields>
                                        {Object.entries(cert)?.map(([key, value]) => (
                                            <Field key={key}>
                                                <span>{t(`pages.register.fields.${key}`)}</span>
                                                <Input value={value} disabled />
                                            </Field>
                                        ))}
                                    </FormFields>
                                </CollapsableContainer>
                                <ButtonsContainer>
                                    <Button type="submit">{t(`pages.register.submit`)}</Button>
                                </ButtonsContainer>
                            </form>
                        )}
                    </CustomContentContainer>
                </AuthContent>
            </MainContainer>
        </>
    );
}
